// src/services/nodesSlice.ts

import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import type { ApiClient } from '@center-technologies/shared-api-clients';

interface FetchNodesArgs {
  endpoint: string;
  apiClient: ApiClient;
}

export const fetchNodes = createAsyncThunk<
  any[],                 // The type of the returned node array
  FetchNodesArgs,        // The thunk’s single argument: endpoint + apiClient
  { rejectValue: string }
>(
  'nodes/fetchNodes',
  async ({ endpoint, apiClient }, thunkAPI) => {
    try {
      const data = await apiClient.get<any[]>(endpoint);
      return data;
    } catch (err: any) {
      console.error('Error fetching nodes:', err);
      return thunkAPI.rejectWithValue(err.message ?? 'Unknown error');
    }
  }
);

interface NodesState {
  selectedSphereId: any;
  currentSphereId: any;
  sphere?: Record<string, unknown>;
  nodes: any[];
  status: 'idle' | 'loading' | 'succeeded' | 'failed';
  error: string | null;
  organizationId: string | null;
}

const initialState: NodesState = {
  sphere: undefined,
  nodes: [],
  status: 'idle',
  error: null,
  organizationId: null,
  selectedSphereId: undefined,
  currentSphereId: undefined
};

const nodesSlice = createSlice({
  name: 'nodes',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchNodes.pending, (state) => {
        state.status = 'loading';
        state.error = null;
      })
      .addCase(fetchNodes.fulfilled, (state, action: PayloadAction<any[]>) => {
        state.status = 'succeeded';
        state.nodes = action.payload;
        // If your backend returns organizationId alongside nodes, assign it here:
        if ((action.payload as any).organizationId) {
          state.organizationId = (action.payload as any).organizationId;
        }
      })
      .addCase(fetchNodes.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload ?? action.error.message ?? null;
      });
  },
});

export default nodesSlice.reducer;
